/** @jsxImportSource @emotion/react */

import { Typography } from '@mui/material'
import { FC, useState } from 'react'
import TabsNavigation from 'components/TabsNavigation/TabsNavigation'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { getPlatformProductConfig, PlatformProductConfig } from 'pagesNew/Products/consts'
import { PlatformSlug } from 'constants/consts'
import NotFound from 'components/NotFound'
import { isEmailsAvailable } from './platformConfig'
import * as classes from './styles'
import { EmailsTabs, getEmailsTabsByConfig } from './consts'
import { FreeMembershipActivation } from './FreeMembershipActivation'
import { PaidMembershipActivation } from './PaidMembershipActivation'
import { MembershipExpiration } from './MembershipExpiration'
import { PasswordReset } from './PasswordReset'
import { PaidMembershipActivationOrSubscriptionStarted } from './PaidMembershipActivationOrSubscriptionStarted'
import { SubscriptionStarted } from './SubscriptionStarted'
import { SubscriptionCanceled } from './SubscriptionCanceled'
import { SubscriptionPaymentFailed } from './SubscriptionPaymentFailed'
import { CrashCoursePurchased } from './CrashCoursePurchased'
import { PauseUnpauseMembership } from './PauseUnpauseMembership'

type EmailTab = { id: EmailsTabs; title: EmailsTabs }

const getTabs = (platformSlug: PlatformSlug): EmailTab[] => {
  const config: PlatformProductConfig = getPlatformProductConfig(platformSlug) ?? {
    regularMemberships: false,
    subscriptionMemberships: false,
    regularExtensions: false,
    pauseTokens: false,
    additionalTests: false,
  }
  return getEmailsTabsByConfig(config, platformSlug).map((item) => ({ id: item, title: item }))
}

const components = {
  [EmailsTabs.FreeMembershipActivation]: FreeMembershipActivation,
  [EmailsTabs.PaidMembershipActivation]: PaidMembershipActivation,
  [EmailsTabs.CrashCoursePurchased]: CrashCoursePurchased,
  [EmailsTabs.MembershipExpiration]: MembershipExpiration,
  [EmailsTabs.PaidMembershipActivationOrSubscriptionStarted]: PaidMembershipActivationOrSubscriptionStarted,
  [EmailsTabs.SubscriptionStarted]: SubscriptionStarted,
  [EmailsTabs.SubscriptionCanceled]: SubscriptionCanceled,
  [EmailsTabs.SubscriptionPaymentFailed]: SubscriptionPaymentFailed,
  [EmailsTabs.PauseUnpauseMembership]: PauseUnpauseMembership,
  [EmailsTabs.PasswordReset]: PasswordReset,
}

type Props = {
  platform: Platform
}

export const Emails: FC = withPlatformCheck(({ platform }: Props) => {
  const tabs = getTabs(platform.slug as PlatformSlug)
  const [activeTab, setActiveTab] = useState<EmailTab>(tabs[0])

  const renderTab = () => {
    const Component = components[activeTab.id]

    if (!Component) return null

    return <Component platform={platform} />
  }

  if (!isEmailsAvailable(platform.slug as PlatformSlug) || !tabs.length || !activeTab) {
    return <NotFound />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        Emails
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={tabs} value={activeTab} onChange={setActiveTab} />

      {renderTab()}
    </section>
  )
})
